import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path as necessary
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSave
} from 'react-icons/fa';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions'; // Adjust path
import { useNavigate } from 'react-router-dom';
import SiteIndentFormModal from './SiteIndentFormModal';
import SiteIndentViewModal from './SiteIndentViewModal';

// Add this component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);
// import SiteIndentViewModal from './SiteIndentViewModal'; // If you create a view modal

const API_URL = '/api'; // Adjust port as necessary

// --- ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// --- STYLES & THEME ---
const COLORS = {
  CLOUD: '#F5EFE6',
  BABY: '#F0ECE2',
  BABY1: '#B88A5A',
  DIVIDER: '#E6D9CC',
  BORDER: '#E6D9CC',
  TEXT: '#211f1eff',
  MUTED: '#6E584A',
  BADGE_BG: '#F6EFE4',
  SUCCESS_BG: '#EAF3EE',
  WARNING_BG: '#FBF2E8',
  PRIMARY_HOVER: '#A27246'
};

// --- ACTION MODAL (Loading / Success / Error) ---
const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;

  let icon, title, bgColor, textColor;

  if (status === 'loading') {
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-600';
    title = 'Processing...';
    icon = <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>;
  } else if (status === 'success') {
    bgColor = 'bg-green-100';
    textColor = 'text-green-600';
    title = 'Done';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    );
  } else {
    // Error
    bgColor = 'bg-red-100';
    textColor = 'text-red-600';
    title = 'Error';
    icon = (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[71]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        {status === 'error' && (
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors">
            Close
          </button>
        )}
      </div>
    </div>
  );
};

// --- CONFIRMATION DIALOG ---
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;
  const iconBgColor = isDanger ? 'bg-red-100' : 'bg-teal-100';
  const iconTextColor = isDanger ? 'text-red-600' : 'text-teal-600';
  const confirmButtonClass = isDanger 
    ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500' 
    : 'bg-teal-700 hover:bg-teal-800 text-white focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onCancel}></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[61]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
          {isDanger ? (
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
          ) : (
            <svg className={`h-6 w-6 ${iconTextColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors focus:outline-none">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium rounded transition-colors focus:outline-none ${confirmButtonClass}`}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

// --- MAIN COMPONENT ---
const SiteIndentItems = () => {
  // CSS Variables injection
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER,
      '--sidebar-bg': '#ffffff',
      '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1,
      '--accent-hover': COLORS.PRIMARY_HOVER,
      '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT,
      '--text-muted': COLORS.MUTED,
      '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3',
      '--search-icon': COLORS.MUTED,
      '--hover-gray': COLORS.BADGE_BG
    };
    Object.entries(themeVars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
  }, []);

  // Scoped CSS
  useEffect(() => {
    const styleId = 'siteindent-overrides';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .siteindent-root .rounded-md, .siteindent-root .rounded-lg { border-radius: 4px !important; }
      .siteindent-root input, .siteindent-root select { background: #ffffff !important; border: 1px solid ${COLORS.BORDER} !important; color: ${COLORS.TEXT} !important; }
      .siteindent-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .siteindent-root table tbody tr { position: relative; }
      .siteindent-root table tbody tr::after { content: ""; position: absolute; left: 0; right: 0; height: 1px; background: ${COLORS.DIVIDER}; bottom: 0; pointer-events: none; }
      .siteindent-root table tbody td { border-bottom: none; position: relative; z-index: 1; }
      .siteindent-root .status-badge { border-radius: 9999px !important; display: inline-flex !important; align-items: center; padding: 0.25rem 0.75rem; font-size: 12px; font-weight: 600; }
      .siteindent-root .sidebar img, .siteindent-root aside img { border-radius: 9999px !important; }
    `;
    document.head.appendChild(style);
  }, []);

  // State
  const [siteIndents, setSiteIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedIndent, setSelectedIndent] = useState(null); // For Edit/View
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

   const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    siteIndentID: null, // Changed from surveyBOQID
    currentLevel: null,
    remarks: ''
  });

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    id: null,
    boqNo: '',
    action: ''
  });

    const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });


    const [summaryData, setSummaryData] = useState({
    indentQty: 0,
    issuedQty: 0,
    pendingIndentQty: 0,
    pendingApproval: 0,
    pendingSendToBC: 0
  });

   const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  
  // Sorting & Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

    // Add these states inside SiteIndentItems component
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    status: 'loading', // 'loading' | 'success' | 'error'
    message: ''
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    isDanger: false
  });

  const navigate = useNavigate();

    const fetchIndentStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/site-indent/quantity-stats-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      
      setSummaryData(prev => ({
        ...prev,
        indentQty: data.indentQty || 0,
        issuedQty: data.issuedQty || 0,
        pendingIndentQty: data.pendingIndentQty || 0
      }));
    } catch (error) {
      // console.error('Error fetching indent stats:', error);
    }
  };

  // Fetch Data
  const fetchSiteIndents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/site-indent-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      // console.log("Fetched Site Indents:", data);
      // setSiteIndents(Array.isArray(data) ? data : []);
      const listData = Array.isArray(data) ? data : [];
      setSiteIndents(listData); // Assuming your state variable is 'siteIndents'

      // --- Calculate Counts from List ---
      const pendingApproval = listData.filter(item => item.ApprovalStatus === 'PENDING').length;
      
      const pendingSendToBC = listData.filter(item => {
        const isApprovedOrNeutral = item.ApprovalStatus !== 'PENDING' && item.ApprovalStatus !== 'REJECTED';
        return isApprovedOrNeutral && !item.SendToBC;
      }).length;

      setSummaryData(prev => ({
        ...prev,
        pendingApproval,
        pendingSendToBC
      }));

    } catch (error) {
      // console.error('Error fetching Site Indents:', error);
      // alert('Error loading data');
      showToast('Error loading data', 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSiteIndents();fetchIndentStats();  }, []);

  // Handlers
  const handleCreate = () => {
    setSelectedIndent(null);
    setShowFormModal(true);
  };

  const handleEdit = async (indent) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/site-indent/${indent.SiteIndentID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch details');
      
      const fullData = await response.json(); 

      setSelectedIndent(fullData); 
      setShowFormModal(true);
      
    } catch (error) {
      // console.error('Error fetching details:', error);
      // alert('Could not load record details');
      showToast('Could not load record details', 'error', 'Error');
    }
  };

    const handleView = async (indent) => {
    try {
      // 1. Fetch the FULL details (this uses the SQL with the JOIN)
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/site-indent/${indent.SiteIndentID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch details');
      
      const fullData = await response.json(); 

      // 2. Pass the FULL data (which includes SurveyBOQNo) to the modal
      setSelectedIndent(fullData); 
      setShowViewModal(true);
      
    } catch (error) {
      // console.error('Error fetching view details:', error);
      // alert('Could not load record details');
      showToast('Could not load record details', 'error', 'Error');
    }
  };

   const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Site Indent',
      message: 'Are you sure you want to delete this Site Indent?',
      isDanger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/site-indent/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();

        if (!response.ok) {
          showToast(
            result.message || 'Delete not allowed',
            'error',
            'Action Not Allowed'
          );
          return;
        }
          // if (!response.ok) throw new Error('Failed to delete');
          fetchSiteIndents();
          showToast('Deleted successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error deleting:', error);
          showToast('Error deleting record', 'error', 'Error');
        }
      }
    });
  };

    // --- NEW HANDLERS ---

  const handleSendToBc = (indent) => {
    // Check if already sent
    const isSentToBC = indent.SendToBC === 1 || indent.SendToBc === 1 || indent.sendtobc === 1;

    if (isSentToBC) {
      setActionModal({ isOpen: true, status: 'error', message: 'This Indent has already been sent to BC.' });
      return;
    }

    // Open Confirmation Modal
    setConfirmModal({
      isOpen: true,
      title: 'Send to BC',
      message: `Are you sure you want to send Indent ${indent.SiteIndentNo} to Business Central?`,
      isDanger: false,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        
        // Open Loading Modal
        setActionModal({ isOpen: true, status: 'loading', message: 'Sending to Business Central...' });

        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/mapping/site-indent/send-to-bc`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ siteIndentID: indent.SiteIndentID })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.error || 'Failed to send to BC');
          }
          
          const result = await response.json();
          
          if (result.success) {
            fetchSiteIndents();
            // Show Success
            setActionModal({ isOpen: true, status: 'success', message: 'Indent sent to BC successfully!' });
            setTimeout(() => setActionModal({ isOpen: false }), 2000);
            // showToast('Indent sent to BC successfully!', 'success', 'Done');
          } else {
            // Show Error
            setActionModal({ isOpen: true, status: 'error', message: 'Error: ' + result.message });
            // showToast('Error: ' + result.message, 'error', 'Error');
          }
        } catch (error) {
          // console.error('Error sending to BC:', error);
          setActionModal({ isOpen: true, status: 'error', message: 'Error sending record to BC: ' + error.message });
          // showToast('Error sending record to BC: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  const openApprovalModal = (siteIndentID, level) => {
    setApprovalModal({
      isOpen: true,
      siteIndentID: siteIndentID,
      currentLevel: level,
      remarks: ''
    });
  };

    const showToast = (message, type = 'success', title = 'Done', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

   const handleApprovalAction = async (action) => {
    const { siteIndentID, remarks } = approvalModal;

    if (action === 'REJECTED' && !remarks.trim()) {
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when rejecting.', 'error', 'Validation Error');
      return;
    }

     if (action === 'APPROVED' && !remarks.trim()) {
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when approving.', 'error', 'Validation Error');
      return;
    }

    // Close the remarks modal immediately
    setApprovalModal({ ...approvalModal, isOpen: false });

    // Open Loading
    setActionModal({ isOpen: true, status: 'loading', message: `${action === 'APPROVED' ? 'Approving' : 'Rejecting'} Site Indent...` });

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = action === 'APPROVED' 
        ? `${API_URL}/site-indent/approve` 
        : `${API_URL}/site-indent/reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ siteIndentID, remarks })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Action failed');
      }
      
      fetchSiteIndents();
      // Show Success
      setActionModal({ isOpen: true, status: 'success', message: `Site Indent ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully` });
      setTimeout(() => setActionModal({ isOpen: false }), 2000);
    } catch (error) {
      // console.error(`Error:`, error);
      // Show Error
      setActionModal({ isOpen: true, status: 'error', message: `Error: ${error.message}` });
    }
  };

  const handleStatusFilterChange = (filterValue) => {
    setSelectedStatusFilters(prev => 
      prev.includes(filterValue) 
        ? prev.filter(item => item !== filterValue) 
        : [...prev, filterValue]
    );
  };

  const handleLogout = () => { navigate('/login'); };


  // Handler to trigger success message
  const handleFormSuccess = (result, actionType) => {
    setTimeout(() => {
      setSuccessModal({
        isOpen: true,
        id: result.SiteIndentID,
        boqNo: result.SiteIndentNo,
        action: actionType // 'Created' or 'Updated'
      });
    }, 300); // Small delay to allow form to start closing
  };

  // Handler to close the success message
  const handleCloseSuccess = () => {
    setSuccessModal({ isOpen: false, id: null, boqNo: '', action: '' });
    fetchSiteIndents(); // Refresh list
  };

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (successModal.isOpen) {
      const timer = setTimeout(() => {
        handleCloseSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen]);

    // Filtering Logic (Replace existing filteredIndents function)
  const filteredIndents = siteIndents.filter(indent => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = [
      indent.SiteIndentNo,
      indent.SurveyBOQNo,
      indent.ProjectNo,
      indent.ProjectDescription,
      indent.VendorCode,
      indent.VendorName,
      indent.ToFeederCode,
      indent.ToFeederDescription,
      indent.TransferType
    ].some(field => field?.toString().toLowerCase().includes(term));
    
    const matchesStatus = statusFilter === 'All' || indent.Status === statusFilter;
    
    // --- UPDATED: Process Status Filter Logic to match JSON structure ---
    const matchesBcStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.some(filter => {
      
      // 1. Approval Phase (Checking ApprovalStatus string)
      if (filter === 'Pending') return indent.ApprovalStatus === 'PENDING';
      if (filter === 'Approved') return indent.ApprovalStatus === 'APPROVED';
      if (filter === 'Rejected') return indent.ApprovalStatus === 'REJECTED';

      // 2. BC Integration Phase
      // "Sent to BC" covers the final state
      if (filter === 'Send to BC')
      return (
        !indent.SendToBc &&
        (indent.ApprovalStatus === 'APPROVED' || indent.ApprovalStatus == null)
      );
      if (filter === 'Data successfully sent') return indent.SendToBc;

      return false;
    });
    // --------------------------------------------------------------

    return matchesSearch && matchesStatus && matchesBcStatus;
  });

  // Sorting Logic
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let valA = a[sortConfig.key] || '';
      let valB = b[sortConfig.key] || '';
      if (sortConfig.key === 'Date' || sortConfig.key === 'CreatedAt') {
        valA = new Date(valA); valB = new Date(valB);
      } else {
        valA = valA.toString().toLowerCase(); valB = valB.toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedIndents = getSortedData(filteredIndents);
  const totalPages = Math.ceil(sortedIndents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedIndents.slice(startIndex, startIndex + itemsPerPage);

  const renderSortArrows = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-xs ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="text-xs ml-1" /> 
      : <FaSortDown className="text-xs ml-1" />;
  };

   const canCreate = hasFullAccess || hasPermission('SITE_INDENT', 'CanCreate');
  const canView = hasFullAccess || hasPermission('SITE_INDENT', 'CanView');
  const canEdit = hasFullAccess || hasPermission('SITE_INDENT', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('SITE_INDENT', 'CanDelete');

  // 🔒 SITE INDENT LOCK RULES
// const isEditLocked = (indent) =>{
//   if (indent.SendToBc === 1 || indent.SendToBc === true) return true;

//   // 2. If Rejected, Lock (Cannot delete)
//   if (indent.ApprovalStatus === 'REJECTED') return true;

//   // 3. If Approved, Lock (Cannot delete)
//   if (indent.ApprovalStatus === 'APPROVED') return true;

//   // 4. If Pending, check the Level
//   if (indent.ApprovalStatus === 'PENDING') {
//     // If exactly Level 1, Allow Delete (Unlocked)
//     if (indent.CurrentLevel === 1) return false;
//      return true;
//   }

//   // 5. Default (Draft/No Flow), Allow Delete
//   return false;
//   }

   // 🔒 SITE INDENT LOCK RULES
  
  // --- EDIT LOGIC: Updated ---
  const isEditLocked = (indent) => {
    // 1. If Sent to BC, always Locked
    if (indent.SendToBc === true || indent.SendToBc === 1) return true;

    // 2. If Rejected, always Locked
    if (indent.ApprovalStatus === 'REJECTED') return true;
     if (!indent.ApprovalFlowID && !indent.SendToBc) {
      return false;
    }

    // 3. If Fully Approved (All levels done), Locked for EVERYONE
    // This ensures neither entry user nor approvers can edit after final approval.
    // if (indent.ApprovalStatus === 'APPROVED') return true;

    // 4. If Pending Approval
    if (indent.ApprovalStatus === 'PENDING' && indent.CurrentLevel > 1) {
      // 'CanCurrentUserApprove' is calculated in SQL.
      // It is TRUE only if the logged-in user/role matches the CurrentApprover of the active level.
      // It effectively restricts edit rights to ONLY the current level's approver.
      if (indent.CanCurrentUserApprove) {
        return false; // Unlocked for this specific approver
      }
      return true; // Locked for Entry User, Previous Approvers, and everyone else
    }

    // 5. Default (Draft/No Flow) - Unlocked
    return false;
  };


  const isDeleteLocked = (indent) => {
    // "Same case with delete"
    
    // 1. If Rejected, always locked
    // if (item.ApprovalStatus === 'REJECTED') return true;

    // 2. If Current Level > 1, locked
    if (indent.CurrentLevel > 1) return true;

    if (indent.ApprovalStatus === 'APPROVED' && indent.DocumentApprovalID) {
      return true;
    }

    // 3. If Level 1 is Approved, locked
    if (indent.CurrentLevel === 1 && indent.ApprovalStatus === 'APPROVED') return true;

    // 4. Specific Logic: "Approved but no DocumentApprovalID"
    if (indent.ApprovalStatus === 'APPROVED' && !indent.DocumentApprovalID) {
      return indent.SendToBc === true || indent.SendToBc === 1 ;
    }

    // 5. Standard Lock: If Sent to BC or Validated
    if (indent.SendToBc === true || indent.SendToBc === 1) return true;

    return false;
  };




  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex siteindent-root">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        
        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Site Indent Management</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Create and manage Site Indents for material transfer.</p>
            </div>
             {canCreate && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
              style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
            >
              <FaPlus className="w-4 h-4" /> Create Indent
            </button> )}
          </div>
        </div>

                {/* Summary Cards */}
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Indent Qty', value: summaryData.indentQty, icon: 'file', color: 'blue' },
            { label: 'Issued Qty', value: summaryData.issuedQty, icon: 'check', color: 'green' },
            { label: 'Pending Indent Qty', value: summaryData.pendingIndentQty, icon: 'clock', color: 'purple' },
            { label: 'Pending Approval', value: summaryData.pendingApproval, icon: 'alert', color: 'amber' },
            { label: 'Pending Send to BC', value: summaryData.pendingSendToBC, icon: 'upload', color: 'teal' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-4 bg-white border shadow-sm hover:shadow-md transition-shadow duration-200 min-h-[100px] flex flex-col"
              style={{ borderColor: COLORS.BORDER }}
            >
              <div className="flex items-start justify-between flex-1">
                <div className="flex-1 min-w-0 pr-3">
                  {/* Label - BIGGER */}
                  <p className="text-md font-bold mb-2" style={{ color: COLORS.TEXT }}>
                    {stat.label}
                  </p>
                  {/* Value - SMALLER with WRAP */}
                  <p 
                    className="text-sm font-semibold leading-snug break-all" 
                    style={{ 
                      color: COLORS.MUTED,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {typeof stat.value === 'number' 
                      ? stat.value.toLocaleString('en-US', { maximumFractionDigits: 2 })
                      : stat.value
                    }
                  </p>
                </div>
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-slate-100 text-slate-600">
                  {stat.icon === 'file' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {stat.icon === 'check' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {stat.icon === 'clock' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {stat.icon === 'alert' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {stat.icon === 'upload' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <input
                type="text"
                placeholder="Indent No, Project, Vendor..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-white border focus:outline-none"
                style={{ borderColor: COLORS.BORDER }}
              />
            </div>
            {/* --- ADDED: BC Status Filter UI --- */}
                       {/* --- UPDATED: BC Status Filter UI --- */}
            <div className="">
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.MUTED }}>Process Status</label>
              <div className="flex flex-wrap gap-4 text-sm px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
                {['Pending', 'Approved', 'Rejected', 'Send to BC','Data successfully sent'].map((label) => (
                  <label key={label} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      checked={selectedStatusFilters.includes(label)}
                      onChange={() => handleStatusFilterChange(label)}
                    />
                    <span style={{ color: COLORS.TEXT }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* ------------------------------------- */}
            {/* ----------------------------------- */}

              {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white border focus:outline-none w-full md:w-auto"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All Status</option>
                <option value="Open">Open</option>
                <option value="Released">Released</option>
                <option value="Pending">Pending</option>
                <option value="Closed">Closed</option>
              </select>
            </div> */}
          </div>
        </div>
        

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Site Indent List</h2>
            <div className="flex items-center space-x-2">
               <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2" style={{color: currentPage === 1 ? '#ccc' : COLORS.TEXT}}><FaChevronLeft /></button>
               <span style={{color: COLORS.TEXT}}>{currentPage} / {totalPages || 1}</span>
               <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-2" style={{color: currentPage === totalPages || totalPages === 0 ? '#ccc' : COLORS.TEXT}}><FaChevronRight /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : currentItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No records found</div>
            ) : (
              <table className="w-full">
                <thead className="text-xs font-semibold text-left uppercase" style={{ backgroundColor: COLORS.BABY, color: COLORS.TEXT, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'SiteIndentNo', direction: sortConfig.key === 'SiteIndentNo' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>
                      Indent No {renderSortArrows('SiteIndentNo')}
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'Date', direction: sortConfig.key === 'Date' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>
                      Date {renderSortArrows('Date')}
                    </th>
                    <th className="px-4 py-3">Project</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">To Feeder</th>
                    <th className="px-4 py-3">Transfer Type</th>
                    <th className='px-4 py-3'>Survey BOQ No</th>
                    {/* <th className="px-4 py-3">Status</th> */}
                    <th className="px-4 py-3">Approval/BC Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((indent, idx) => (
                    <tr key={indent.SiteIndentID || idx} className="hover:bg-[#faf9f8] transition-colors">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.TEXT }}>{indent.SiteIndentNo}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{formatDate(indent.Date)}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{indent.ProjectNo}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{indent.ProjectDescription || '-'}</div>
                        {/* <div className="text-xs">ID: {indent.ProjectHeaderId}</div> */}
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                           <div className="font-medium text-black">{indent.VendorCode || '-'}</div>
                           <div className="text-xs text-gray-500 truncate max-w-[200px]">{indent.VendorName || '-'}</div>
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                          <div className="font-medium text-black">{indent.ToFeederCode}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[200px]">{indent.ToFeederDescription || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: COLORS.MUTED }}>
                        {indent.TransferType}
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        {indent.SurveyBOQNo }
                      </td>
                      {/* <td className="px-4 py-3">
                        <span className={`status-badge ${
                          indent.Status === 'Open' ? 'bg-green-100 text-green-800' : 
                          indent.Status === 'Released' ? 'bg-blue-100 text-blue-800' : 
                          indent.Status === 'Closed' ? 'bg-gray-100 text-gray-800' : 
                          'bg-yellow-100 text-yellow-800' 
                        }`}>
                          {indent.Status || 'Draft'}
                        </span>
                      </td> */}
                      <td className='px-4 py-3'>
                    {/*
                      LOGIC MIRRORING SURVEY BOQ:
                      1. PENDING    -> Approval flow
                      2. REJECTED   -> Show Rejected only
                      3. APPROVED / NULL -> Send to BC flow
                    */}

                    {indent.ApprovalStatus === 'PENDING' ? (
                      // --- APPROVAL PHASE ---
                      indent.CanCurrentUserApprove ? (
                        <button 
                          onClick={() => openApprovalModal(indent.SiteIndentID, indent.CurrentLevel)}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
                          style={{ backgroundColor: '#16A34A' }} // Green
                        >
                          Approve (Lvl {indent.CurrentLevel})
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500 italic flex items-center gap-1">
                          Approval is going on (Lvl {indent.CurrentLevel})
                        </span>
                      )

                    ) : indent.ApprovalStatus === 'REJECTED' ? (
                      // --- REJECTED STATE ---
                      <span className="text-xs font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-200">
                        Rejected
                      </span>

                    ) : (
                      // --- POST-APPROVAL PHASE ---
                      <>
                        {/* If the Approval workflow is done (or doesn't exist) but not yet sent to BC */}
                        {(!indent.SendToBc || indent.SendToBc === 0) && (
                          <button 
                            onClick={() => handleSendToBc(indent)}
                            className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
                            style={{ backgroundColor: '#2563EB' }} // Blue
                            title="Send to BC"
                          >
                            Send to BC
                          </button>
                        )}

                        {/* If Sent to BC */}
                        {(indent.SendToBc === 1 || indent.SendToBc === true) && (
                          <span className="text-xs font-semibold text-green-600 flex items-center gap-1 bg-green-50 px-2 py-1 rounded border border-green-200">
                            Sent to BC successfully.
                          </span>
                        )}
                      </>
                    )}
                  </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center space-x-3" style={{ color: COLORS.MUTED }}>
                         {canView && (<button onClick={() => handleView(indent)} title="View" className="hover:text-black"><FaEye /></button>)} 
                          {canEdit && (
  <button
    onClick={() => !isEditLocked(indent) && handleEdit(indent)}
    disabled={isEditLocked(indent)}
    title={
      isEditLocked(indent)
        ? 'Edit disabled after approval or rejection'
        : 'Edit'
    }
    className={`${
      isEditLocked(indent)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-blue-600'
    }`}
  >
    <FaEdit />
  </button>
)}

                          {canDelete && (
  <button
    onClick={() =>
      !isDeleteLocked(indent) &&
      handleDelete(indent.SiteIndentID)
    }
    disabled={isDeleteLocked(indent)}
    title={
      isDeleteLocked(indent)
        ? 'Delete disabled after approval / rejection / BC'
        : 'Delete'
    }
    className={`${
      isDeleteLocked(indent)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-red-600'
    }`}
  >
    <FaTrash />
  </button>
)}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>

      {/* FORM MODAL */}
      {showFormModal && (
        <SiteIndentFormModal
          open={showFormModal}
          onClose={() => setShowFormModal(false)}
          initialData={selectedIndent}
          onSubmit={fetchSiteIndents}
          onFormSuccess={handleFormSuccess}
        />
      )}

      {/* VIEW MODAL */}
      {showViewModal && selectedIndent && (
        <SiteIndentViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          data={selectedIndent}
        />
      )}

        {/* Approval/Reject Modal */}
      {approvalModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50">
          <div className="relative bg-white shadow-xl max-w-md w-full mx-4 p-6 rounded-lg animate-in fade-in zoom-in duration-200">
            {/* FIXED: Changed 'Review MRN' to 'Review Site Indent' and used siteIndentID */}
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Review Site Indent: {approvalModal.siteIndentID} (Level {approvalModal.currentLevel})
            </h3>
            
            {/* Remarks Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks {<span className="text-red-500">*</span>}
              </label>
              <textarea
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter remarks..."
                value={approvalModal.remarks}
                onChange={(e) => setApprovalModal({ ...approvalModal, remarks: e.target.value })}
              ></textarea>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprovalAction('REJECTED')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprovalAction('APPROVED')}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* --- SUCCESS MODAL --- */}
      {successModal.isOpen && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-slideDown">
          <div className="flex items-center gap-4 bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 min-w-[420px]">
            {/* Icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <SuccessIcon size={22} />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800">
                Site Indent {successModal.action} Successfully
              </h4>
              <p className="text-xs text-gray-500">
                Indent No: <span className="font-medium">{successModal.boqNo}</span>
              </p>
            </div>

            {/* Loader bar */}
            <div className="absolute bottom-0 left-0 h-[3px] bg-green-500 rounded-b-xl animate-progress" />
          </div>

          <style>
            {`
              @keyframes slideDown {
                from { opacity: 0; transform: translate(-50%, -10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
              @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
              }
              .animate-slideDown { animation: slideDown 0.25s ease-out; }
              .animate-progress { animation: progress 2s linear; }

            `}
          </style>
        </div>
      )}

            {/* --- TOAST NOTIFICATION --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`
            relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
            ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
            ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
            ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
          `}>
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
          <style>
            {`
             
                /* Add this to the end of your existing style.innerHTML string */
                @keyframes toastIn {
                  from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
                  to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes toastProgress {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .animate-toastIn { animation: toastIn 0.25s ease-out; }
                .animate-toastProgress { animation: toastProgress 4s linear forwards; }

            `}
          </style>
        </div>
      )}

      {/* Action Modal (Loading / Success / Error) */}
      <ActionModal 
        isOpen={actionModal.isOpen}
        status={actionModal.status}
        message={actionModal.message}
        onClose={() => setActionModal({ ...actionModal, isOpen: false })}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog 
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        isDanger={confirmModal.isDanger}
      />


    </div>
  );
};

export default SiteIndentItems;